import { SLUG_REGEX } from './contracts/setup/slug';

/**
 * Fold combining diacritical marks onto their ASCII base letter so accented
 * text survives slugging — "Negociação" -> "negociacao", not "negocia_o".
 * MUST be applied BEFORE any `[^a-z0-9]` stripping, otherwise the accented
 * letter is dropped to a separator instead of its base letter.
 */
export function foldDiacritics(input: string): string {
  return input.normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

/**
 * THE canonical kebab-case slug producer for URL/folder/file names
 * (`[a-z0-9-]`). Every kebab caller — frontend tag/skill inputs, the shared
 * `slugifySkillName`, both backend hub-folder paths, and the mirrored CLI
 * `slugify` — MUST produce identical output for the same input. Do not
 * reimplement; route through this (or, for the CLI which can't import the
 * shared runtime, mirror it and pin with the drift-guard test).
 *
 * NFKD (via `foldDiacritics`) so accents fold to their base letter AND
 * compatibility chars collapse toward ASCII ("Clínica Médica" -> "clinica-medica",
 * "Eﬃcient" -> "efficient"). Then lowercase, runs of non-alphanumerics -> one
 * hyphen, cap, and trim edge hyphens AFTER the cut (a mid-separator cut must not
 * leave a trailing hyphen).
 *
 * `maxLength` is a parameter, not a constant: tags cap at 50, skill names and
 * hub-folder slugs at 64. The CLI⇄backend hub-folder cap is 64 — both sides
 * MUST pass the same value or folder resolution diverges.
 *
 * This is distinct from `slugify` below, which produces snake_case identifier
 * slugs for state/kanban (`[a-z0-9_]`, must start with a letter) because those
 * become ClickHouse JSON paths where hyphens are invalid. The two are not
 * interchangeable.
 */
export function slugifyKebab(input: string, maxLength = 64): string {
  if (typeof input !== 'string') return '';
  return foldDiacritics(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, maxLength)
    .replace(/^-|-$/g, ''); // trim AFTER slicing — a mid-separator cut must not leave an edge hyphen
}

export function slugify(name: string): string {
  if (typeof name !== 'string') return 'state';
  let s = foldDiacritics(name)
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  s = s.replace(/^[^a-z]+/, '');
  if (s.length === 0) s = 'state';
  // Trim AFTER slicing — a cap landing on a separator must not leave a trailing '_'.
  if (s.length > 50) s = s.slice(0, 50).replace(/_+$/g, '');
  if (!SLUG_REGEX.test(s)) s = `state_${s}`.slice(0, 50).replace(/_+$/g, '');
  return s;
}

// Mutates `used` by adding the chosen slug.
export function ensureUniqueSlug(slug: string, used: Set<string>): string {
  if (!used.has(slug)) {
    used.add(slug);
    return slug;
  }
  for (let i = 2; i < 1_000_000; i++) {
    const suffix = `_${i}`;
    const baseLen = Math.max(1, 50 - suffix.length);
    const candidate = `${slug.slice(0, baseLen)}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
  throw new Error(`slug collision space exhausted for "${slug}"`);
}
